import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Globe, ChevronDown, Check } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Google OAuth Client ID for One Tap
const GOOGLE_CLIENT_ID = "512584323191-s11us3d581ishufvishrrut8lf7ll84f.apps.googleusercontent.com";

// DBPRO Brand Colors
const BRAND = {
  navyDark: '#0A1A2F',
  navyMedium: '#0F2847',
  inputBg: '#1A3A5C',
  red: '#E53935',
  redHover: '#C62828',
  white: '#FFFFFF',
  lightGray: '#B0BEC5',
  blue: '#2196F3',
  error: '#EF5350'
};

// Logo URL
const DBPRO_LOGO_URL = 'https://customer-assets.emergentagent.com/job_gps-tracker-dash-1/artifacts/awchbld5_Final%20brand%20logo.png';

// Translations
const TRANSLATIONS = {
  en: {
    welcome: 'Welcome back',
    subtitle: 'Sign in to Dragonboat Pro Tracker',
    email: 'Email',
    emailPlaceholder: 'you@example.com',
    password: 'Password',
    passwordPlaceholder: 'Enter your password',
    confirmPassword: 'Confirm Password',
    confirmPasswordPlaceholder: 'Re-enter your password',
    errorConfirmPassword: 'Passwords do not match. Please try again.',
    rememberMe: 'Remember me',
    forgotPassword: 'Forgot password?',
    signIn: 'Log In',
    noAccount: "Don't have an account?",
    signUp: 'Sign Up',
    or: 'OR',
    continueGoogle: 'Continue with Google',
    slogan: 'TRAIN. TRACK. WIN.',
    errorEmail: 'Enter a valid email address.',
    errorPassword: 'Password must be at least 8 characters.',
    errorAuth: 'Incorrect email or password. Please try again.',
    signingIn: 'Signing in...',
    registering: 'Creating account...',
    registerTitle: 'Create Account',
    registerSubtitle: 'Join Dragonboat Pro Tracker',
    name: 'Name',
    namePlaceholder: 'Your full name',
    createAccount: 'Create Account',
    haveAccount: 'Already have an account?',
    backToLogin: 'Sign In',
    errorName: 'Please enter your name.',
    errorEmailExists: 'This email is already registered.',
    successRegister: 'Account created! Logging you in...',
    forgotPasswordTitle: 'Reset Password',
    forgotPasswordSubtitle: 'Enter your email to receive a reset link',
    sendResetLink: 'Send Reset Link',
    sending: 'Sending...',
    resetEmailSent: 'If an account exists with this email, you will receive a reset link.',
    backToLoginLink: 'Back to Login'
  },
  zh: {
    welcome: '欢迎回来',
    subtitle: '登录龙舟专业追踪器',
    email: '电子邮箱',
    emailPlaceholder: 'you@example.com',
    password: '密码',
    passwordPlaceholder: '请输入密码',
    confirmPassword: '确认密码',
    confirmPasswordPlaceholder: '请再次输入密码',
    errorConfirmPassword: '密码不匹配，请重试。',
    rememberMe: '记住我',
    forgotPassword: '忘记密码？',
    signIn: '登录',
    noAccount: '没有账户？',
    signUp: '注册',
    or: '或',
    continueGoogle: '使用Google继续',
    slogan: '训练。追踪。获胜。',
    errorEmail: '请输入有效的电子邮箱地址。',
    errorPassword: '密码至少需要8个字符。',
    errorAuth: '邮箱或密码错误，请重试。',
    signingIn: '登录中...',
    registering: '创建账户中...',
    registerTitle: '创建账户',
    registerSubtitle: '加入龙舟专业追踪器',
    name: '姓名',
    namePlaceholder: '您的全名',
    createAccount: '创建账户',
    haveAccount: '已有账户？',
    backToLogin: '登录',
    errorName: '请输入您的姓名。',
    errorEmailExists: '此邮箱已注册。',
    successRegister: '账户已创建！正在登录...',
    forgotPasswordTitle: '重置密码',
    forgotPasswordSubtitle: '输入您的邮箱以接收重置链接',
    sendResetLink: '发送重置链接',
    sending: '发送中...',
    resetEmailSent: '如果此邮箱存在账户，您将收到重置链接。',
    backToLoginLink: '返回登录'
  },
  es: {
    welcome: 'Bienvenido de nuevo',
    subtitle: 'Inicia sesión en Dragonboat Pro Tracker',
    email: 'Correo electrónico',
    emailPlaceholder: 'tu@ejemplo.com',
    password: 'Contraseña',
    passwordPlaceholder: 'Ingresa tu contraseña',
    confirmPassword: 'Confirmar Contraseña',
    confirmPasswordPlaceholder: 'Vuelve a ingresar tu contraseña',
    errorConfirmPassword: 'Las contraseñas no coinciden. Por favor, inténtalo de nuevo.',
    rememberMe: 'Recuérdame',
    forgotPassword: '¿Olvidaste tu contraseña?',
    signIn: 'Iniciar sesión',
    noAccount: '¿No tienes una cuenta?',
    signUp: 'Regístrate',
    or: 'O',
    continueGoogle: 'Continuar con Google',
    slogan: 'ENTRENA. RASTREA. GANA.',
    errorEmail: 'Ingresa una dirección de correo válida.',
    errorPassword: 'La contraseña debe tener al menos 8 caracteres.',
    errorAuth: 'Correo o contraseña incorrectos. Por favor, intenta de nuevo.',
    signingIn: 'Iniciando sesión...',
    registering: 'Creando cuenta...',
    registerTitle: 'Crear Cuenta',
    registerSubtitle: 'Únete a Dragonboat Pro Tracker',
    name: 'Nombre',
    namePlaceholder: 'Tu nombre completo',
    createAccount: 'Crear Cuenta',
    haveAccount: '¿Ya tienes una cuenta?',
    backToLogin: 'Iniciar sesión',
    errorName: 'Por favor ingresa tu nombre.',
    errorEmailExists: 'Este correo ya está registrado.',
    successRegister: '¡Cuenta creada! Iniciando sesión...',
    forgotPasswordTitle: 'Restablecer Contraseña',
    forgotPasswordSubtitle: 'Ingresa tu correo para recibir un enlace de restablecimiento',
    sendResetLink: 'Enviar Enlace',
    sending: 'Enviando...',
    resetEmailSent: 'Si existe una cuenta con este correo, recibirás un enlace de restablecimiento.',
    backToLoginLink: 'Volver a Iniciar Sesión'
  },
  fr: {
    welcome: 'Bon retour',
    subtitle: 'Connectez-vous à Dragonboat Pro Tracker',
    email: 'E-mail',
    emailPlaceholder: 'vous@exemple.com',
    password: 'Mot de passe',
    passwordPlaceholder: 'Entrez votre mot de passe',
    confirmPassword: 'Confirmer le mot de passe',
    confirmPasswordPlaceholder: 'Entrez à nouveau votre mot de passe',
    errorConfirmPassword: 'Les mots de passe ne correspondent pas. Veuillez réessayer.',
    rememberMe: 'Se souvenir de moi',
    forgotPassword: 'Mot de passe oublié?',
    signIn: 'Se connecter',
    noAccount: "Vous n'avez pas de compte?",
    signUp: "S'inscrire",
    or: 'OU',
    continueGoogle: 'Continuer avec Google',
    slogan: "S'ENTRAÎNER. SUIVRE. GAGNER.",
    errorEmail: 'Entrez une adresse e-mail valide.',
    errorPassword: 'Le mot de passe doit contenir au moins 8 caractères.',
    errorAuth: 'E-mail ou mot de passe incorrect. Veuillez réessayer.',
    signingIn: 'Connexion en cours...',
    registering: 'Création du compte...',
    registerTitle: 'Créer un Compte',
    registerSubtitle: 'Rejoignez Dragonboat Pro Tracker',
    name: 'Nom',
    namePlaceholder: 'Votre nom complet',
    createAccount: 'Créer un Compte',
    haveAccount: 'Vous avez déjà un compte?',
    backToLogin: 'Se connecter',
    errorName: 'Veuillez entrer votre nom.',
    errorEmailExists: 'Cet e-mail est déjà enregistré.',
    successRegister: 'Compte créé ! Connexion en cours...',
    forgotPasswordTitle: 'Réinitialiser le Mot de Passe',
    forgotPasswordSubtitle: 'Entrez votre e-mail pour recevoir un lien de réinitialisation',
    sendResetLink: 'Envoyer le Lien',
    sending: 'Envoi en cours...',
    resetEmailSent: 'Si un compte existe avec cet e-mail, vous recevrez un lien de réinitialisation.',
    backToLoginLink: 'Retour à la Connexion'
  },
  de: {
    welcome: 'Willkommen zurück',
    subtitle: 'Melden Sie sich bei Dragonboat Pro Tracker an',
    email: 'E-Mail',
    emailPlaceholder: 'du@beispiel.com',
    password: 'Passwort',
    passwordPlaceholder: 'Geben Sie Ihr Passwort ein',
    confirmPassword: 'Passwort bestätigen',
    confirmPasswordPlaceholder: 'Geben Sie Ihr Passwort erneut ein',
    errorConfirmPassword: 'Passwörter stimmen nicht überein. Bitte versuchen Sie es erneut.',
    rememberMe: 'Angemeldet bleiben',
    forgotPassword: 'Passwort vergessen?',
    signIn: 'Anmelden',
    noAccount: 'Noch kein Konto?',
    signUp: 'Registrieren',
    or: 'ODER',
    continueGoogle: 'Mit Google fortfahren',
    slogan: 'TRAINIEREN. VERFOLGEN. GEWINNEN.',
    errorEmail: 'Geben Sie eine gültige E-Mail-Adresse ein.',
    errorPassword: 'Das Passwort muss mindestens 8 Zeichen haben.',
    errorAuth: 'Falsche E-Mail oder Passwort. Bitte versuchen Sie es erneut.',
    signingIn: 'Anmeldung...',
    registering: 'Konto wird erstellt...',
    registerTitle: 'Konto Erstellen',
    registerSubtitle: 'Treten Sie Dragonboat Pro Tracker bei',
    name: 'Name',
    namePlaceholder: 'Ihr vollständiger Name',
    createAccount: 'Konto Erstellen',
    haveAccount: 'Haben Sie bereits ein Konto?',
    backToLogin: 'Anmelden',
    errorName: 'Bitte geben Sie Ihren Namen ein.',
    errorEmailExists: 'Diese E-Mail ist bereits registriert.',
    successRegister: 'Konto erstellt! Sie werden angemeldet...',
    forgotPasswordTitle: 'Passwort Zurücksetzen',
    forgotPasswordSubtitle: 'Geben Sie Ihre E-Mail ein, um einen Reset-Link zu erhalten',
    sendResetLink: 'Link Senden',
    sending: 'Wird gesendet...',
    resetEmailSent: 'Wenn ein Konto mit dieser E-Mail existiert, erhalten Sie einen Reset-Link.',
    backToLoginLink: 'Zurück zur Anmeldung'
  }
};

const LANGUAGES = [
  { code: 'en', label: 'English', short: 'EN' },
  { code: 'zh', label: '中文 (简体)', short: '中文' },
  { code: 'es', label: 'Español', short: 'ES' },
  { code: 'fr', label: 'Français', short: 'FR' },
  { code: 'de', label: 'Deutsch', short: 'DE' }
];

const LoginScreen_DBPRO = () => {
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  
  // State
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('dbpro_language') || 'en';
  });
  const [showLangDropdown, setShowLangDropdown] = useState(false);
  const [email, setEmail] = useState(() => {
    return localStorage.getItem('dbpro_remember_email') || '';
  });
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(() => {
    return !!localStorage.getItem('dbpro_remember_email');
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({ email: '', password: '', name: '', confirmPassword: '', auth: '' });
  const [isRegisterMode, setIsRegisterMode] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotEmailSent, setForgotEmailSent] = useState(false);
  const [forgotError, setForgotError] = useState('');
  
  const t = TRANSLATIONS[language];
  const currentLang = LANGUAGES.find(l => l.code === language);
  
  // Google One Tap state
  const [googleLoading, setGoogleLoading] = useState(false);
  const googleInitialized = useRef(false);

  // Handle Google One Tap credential response
  const handleGoogleCredentialResponse = useCallback(async (response) => {
    if (googleLoading) return;
    setGoogleLoading(true);
    setErrors({ email: '', password: '', name: '', confirmPassword: '', auth: '' });
    
    try {
      // Send credential to backend for verification
      const res = await fetch(`${API}/auth/google-onetap`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credential: response.credential })
      });
      
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Google sign-in failed');
      }
      
      const data = await res.json();
      
      // Store session token in localStorage
      if (data.session_token) {
        localStorage.setItem('dbpro_session_token', data.session_token);
      }
      localStorage.setItem('dbpro_user', JSON.stringify(data));
      
      // Navigate to dashboard
      navigate('/dashboard', { replace: true, state: { user: data } });
    } catch (error) {
      console.error('Google One Tap error:', error);
      setErrors({ ...errors, auth: error.message || 'Google sign-in failed. Please try again.' });
      setGoogleLoading(false);
    }
  }, [navigate, googleLoading, errors]);

  // Initialize Google Identity Services
  useEffect(() => {
    if (googleInitialized.current) return;
    
    // Load Google Identity Services script
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (window.google && !googleInitialized.current) {
        googleInitialized.current = true;
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleGoogleCredentialResponse,
          auto_select: false,
          cancel_on_tap_outside: true
        });
      }
    };
    document.body.appendChild(script);
    
    return () => {
      // Cleanup on unmount
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, [handleGoogleCredentialResponse]);

  // Handle Google login button click - trigger One Tap prompt
  const handleGoogleLogin = () => {
    if (googleLoading) return;
    
    if (window.google?.accounts?.id) {
      // Trigger the One Tap prompt
      window.google.accounts.id.prompt((notification) => {
        if (notification.isNotDisplayed()) {
          // One Tap not displayed - fall back to popup
          console.log('One Tap not displayed, reason:', notification.getNotDisplayedReason());
          // Try button flow as fallback
          window.google.accounts.id.renderButton(
            document.getElementById('google-signin-fallback'),
            { 
              type: 'standard',
              theme: 'outline',
              size: 'large',
              text: 'continue_with',
              width: 320
            }
          );
        } else if (notification.isSkippedMoment()) {
          console.log('One Tap skipped, reason:', notification.getSkippedReason());
        } else if (notification.isDismissedMoment()) {
          console.log('One Tap dismissed, reason:', notification.getDismissedReason());
        }
      });
    } else {
      setErrors({ ...errors, auth: 'Google Sign-In is loading. Please try again.' });
    }
  };
  useEffect(() => {
    const checkExistingAuth = async () => {
      try {
        const response = await fetch(`${API}/auth/me`, {
        });
        
        if (response.ok) {
          // Already authenticated, redirect to dashboard
          navigate('/dashboard', { replace: true });
        }
      } catch (error) {
        // Not authenticated, stay on login page
      }
    };
    
    checkExistingAuth();
  }, [navigate]);

  // Persist language choice
  useEffect(() => {
    localStorage.setItem('dbpro_language', language);
  }, [language]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowLangDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Email validation
  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  // Handle email/password login
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Reset errors
    setErrors({ email: '', password: '', name: '', confirmPassword: '', auth: '' });
    setSuccessMessage('');
    
    // Validate
    let hasError = false;
    const newErrors = { email: '', password: '', name: '', confirmPassword: '', auth: '' };
    
    if (!email || !validateEmail(email)) {
      newErrors.email = t.errorEmail;
      hasError = true;
    }
    
    if (!password || password.length < 8) {
      newErrors.password = t.errorPassword;
      hasError = true;
    }
    
    if (isRegisterMode && !name.trim()) {
      newErrors.name = t.errorName;
      hasError = true;
    }
    
    // Confirm password validation (only for registration)
    if (isRegisterMode && password !== confirmPassword) {
      newErrors.confirmPassword = t.errorConfirmPassword;
      hasError = true;
    }
    
    if (hasError) {
      setErrors(newErrors);
      return;
    }
    
    setIsLoading(true);
    
    try {
      const endpoint = isRegisterMode ? '/auth/register' : '/auth/login';
      const body = isRegisterMode 
        ? { email, password, name } 
        : { email, password };
      
      const response = await fetch(`${API}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      
      if (!response.ok) {
        const data = await response.json();
        if (response.status === 409) {
          setErrors({ ...newErrors, email: t.errorEmailExists });
        } else if (!isRegisterMode && data.detail === 'no_account') {
          setErrors({ ...newErrors, auth: 'No account found with this email. Tap "Sign Up" below to create one.' });
        } else if (!isRegisterMode && data.detail === 'wrong_password') {
          setErrors({ ...newErrors, auth: 'Incorrect password. Please try again.' });
        } else {
          setErrors({ ...newErrors, auth: data.detail || t.errorAuth });
        }
        setIsLoading(false);
        return;
      }
      
      const user = await response.json();
      
      // Store session token in localStorage (backup for browsers that block cookies)
      if (user.session_token) {
        localStorage.setItem('dbpro_session_token', user.session_token);
      }
      localStorage.setItem('dbpro_user', JSON.stringify(user));
      
      // Store remember me preference
      if (rememberMe) {
        localStorage.setItem('dbpro_remember_email', email);
      } else {
        localStorage.removeItem('dbpro_remember_email');
      }
      
      if (isRegisterMode) {
        setSuccessMessage(t.successRegister);
        setTimeout(() => {
          navigate('/dashboard', { state: { user } });
        }, 1000);
      } else {
        navigate('/dashboard', { state: { user } });
      }
    } catch (error) {
      console.error('Auth error:', error);
      setErrors({ ...newErrors, auth: t.errorAuth });
    }
    
    setIsLoading(false);
  };

  // Toggle between login and register
  const toggleMode = () => {
    setIsRegisterMode(!isRegisterMode);
    setErrors({ email: '', password: '', name: '', confirmPassword: '', auth: '' });
    setSuccessMessage('');
    setConfirmPassword('');
  };

  // Handle forgot password
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotError('');
    
    if (!forgotEmail || !validateEmail(forgotEmail)) {
      setForgotError(t.errorEmail);
      return;
    }
    
    setIsLoading(true);
    
    try {
      const response = await fetch(`${API}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail })
      });
      
      if (response.ok) {
        setForgotEmailSent(true);
      } else {
        const data = await response.json();
        setForgotError(data.detail || 'Failed to send reset email');
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      setForgotError('An error occurred. Please try again.');
    }
    
    setIsLoading(false);
  };

  // Open forgot password modal
  const openForgotPassword = () => {
    setShowForgotPassword(true);
    setForgotEmail(email); // Pre-fill with login email if available
    setForgotEmailSent(false);
    setForgotError('');
  };

  // Close forgot password modal
  const closeForgotPassword = () => {
    setShowForgotPassword(false);
    setForgotEmail('');
    setForgotEmailSent(false);
    setForgotError('');
  };

  return (
    <div 
      className="min-h-screen flex flex-col relative"
      style={{ backgroundColor: BRAND.navyDark }}
      data-testid="login-screen"
    >
      {/* Language Selector */}
      <div className="absolute top-4 right-4" ref={dropdownRef}>
        <button
          onClick={() => setShowLangDropdown(!showLangDropdown)}
          className="flex items-center gap-2 px-3 py-2 rounded-lg transition-colors"
          style={{ color: BRAND.white }}
          data-testid="language-selector"
        >
          <Globe className="w-4 h-4" />
          <span className="text-sm font-medium">{currentLang?.short}</span>
          <ChevronDown className={`w-4 h-4 transition-transform ${showLangDropdown ? 'rotate-180' : ''}`} />
        </button>
        
        {showLangDropdown && (
          <div 
            className="absolute right-0 mt-2 w-48 rounded-lg shadow-lg overflow-hidden z-50"
            style={{ backgroundColor: BRAND.navyMedium }}
            data-testid="language-dropdown"
          >
            {LANGUAGES.map((lang) => (
              <button
                key={lang.code}
                onClick={() => {
                  setLanguage(lang.code);
                  setShowLangDropdown(false);
                }}
                className="w-full px-4 py-3 text-left flex items-center justify-between hover:bg-opacity-80 transition-colors"
                style={{ 
                  backgroundColor: language === lang.code ? BRAND.inputBg : 'transparent',
                  color: BRAND.white 
                }}
                data-testid={`lang-option-${lang.code}`}
              >
                <span className="text-sm">{lang.label}</span>
                {language === lang.code && <Check className="w-4 h-4" style={{ color: BRAND.blue }} />}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12">
        {/* Logo */}
        <img
          src={DBPRO_LOGO_URL}
          alt="DBPRO Logo"
          className="h-16 md:h-20 w-auto object-contain mb-8"
          data-testid="login-logo"
        />

        {/* Welcome Text */}
        <h1 
          className="text-2xl md:text-3xl font-bold mb-2 text-center"
          style={{ color: BRAND.white }}
          data-testid="welcome-title"
        >
          {isRegisterMode ? t.registerTitle : t.welcome}
        </h1>
        <p 
          className="text-base mb-8 text-center"
          style={{ color: BRAND.lightGray }}
          data-testid="welcome-subtitle"
        >
          {isRegisterMode ? t.registerSubtitle : t.subtitle}
        </p>

        {/* Success Message */}
        {successMessage && (
          <div 
            className="w-full max-w-sm mb-4 p-3 rounded-lg text-center"
            style={{ backgroundColor: '#4CAF5020', color: '#4CAF50' }}
            data-testid="success-message"
          >
            {successMessage}
          </div>
        )}

        {/* Auth Error */}
        {errors.auth && (
          <div 
            className="w-full max-w-sm mb-4 p-3 rounded-lg text-center"
            style={{ backgroundColor: `${BRAND.error}20`, color: BRAND.error }}
            data-testid="auth-error"
          >
            {errors.auth}
          </div>
        )}

        {/* Google Login - Primary CTA */}
        <button
          onClick={handleGoogleLogin}
          disabled={isLoading || googleLoading}
          className="w-full max-w-sm py-3 rounded-lg text-base font-medium flex items-center justify-center gap-3 transition-all disabled:opacity-70 mb-2"
          style={{ 
            backgroundColor: BRAND.white,
            color: '#333'
          }}
          data-testid="google-login-button"
        >
          {googleLoading ? (
            <>
              <div className="w-5 h-5 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
              Signing in...
            </>
          ) : (
            <>
              {/* Google Icon */}
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              {t.continueGoogle}
            </>
          )}
        </button>
        
        {/* Hidden fallback container for Google rendered button */}
        <div id="google-signin-fallback" className="w-full max-w-sm flex justify-center mb-4" />

        {/* OR Divider */}
        <div className="flex items-center gap-4 w-full max-w-sm mb-6">
          <div className="flex-1 h-px" style={{ backgroundColor: BRAND.lightGray }} />
          <span className="text-sm" style={{ color: BRAND.lightGray }}>{t.or}</span>
          <div className="flex-1 h-px" style={{ backgroundColor: BRAND.lightGray }} />
        </div>

        {/* Login/Register Form */}
        <form onSubmit={handleSubmit} className="w-full max-w-sm space-y-4">
          {/* Name Field (Register only) */}
          {isRegisterMode && (
            <div>
              <label 
                className="block text-sm font-medium mb-2"
                style={{ color: BRAND.white }}
              >
                {t.name}
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (errors.name) setErrors({ ...errors, name: '' });
                }}
                placeholder={t.namePlaceholder}
                className="w-full px-4 py-3 rounded-lg text-base focus:outline-none focus:ring-2"
                style={{ 
                  backgroundColor: BRAND.inputBg,
                  color: BRAND.white,
                  borderColor: errors.name ? BRAND.error : 'transparent',
                  borderWidth: '1px'
                }}
                data-testid="name-input"
              />
              {errors.name && (
                <p className="mt-1 text-sm" style={{ color: BRAND.error }} data-testid="name-error">
                  {errors.name}
                </p>
              )}
            </div>
          )}

          {/* Email Field */}
          <div>
            <label 
              className="block text-sm font-medium mb-2"
              style={{ color: BRAND.white }}
            >
              {t.email}
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (errors.email) setErrors({ ...errors, email: '' });
              }}
              placeholder={t.emailPlaceholder}
              className="w-full px-4 py-3 rounded-lg text-base focus:outline-none focus:ring-2"
              style={{ 
                backgroundColor: BRAND.inputBg,
                color: BRAND.white,
                borderColor: errors.email ? BRAND.error : 'transparent',
                borderWidth: '1px'
              }}
              data-testid="email-input"
            />
            {errors.email && (
              <p className="mt-1 text-sm" style={{ color: BRAND.error }} data-testid="email-error">
                {errors.email}
              </p>
            )}
          </div>

          {/* Password Field */}
          <div>
            <label 
              className="block text-sm font-medium mb-2"
              style={{ color: BRAND.white }}
            >
              {t.password}
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (errors.password) setErrors({ ...errors, password: '' });
                }}
                placeholder={t.passwordPlaceholder}
                className="w-full px-4 py-3 pr-12 rounded-lg text-base focus:outline-none focus:ring-2"
                style={{ 
                  backgroundColor: BRAND.inputBg,
                  color: BRAND.white,
                  borderColor: errors.password ? BRAND.error : 'transparent',
                  borderWidth: '1px'
                }}
                data-testid="password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2"
                style={{ color: BRAND.lightGray }}
                data-testid="password-toggle"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            {errors.password && (
              <p className="mt-1 text-sm" style={{ color: BRAND.error }} data-testid="password-error">
                {errors.password}
              </p>
            )}
          </div>

          {/* Confirm Password Field (Register only) */}
          {isRegisterMode && (
            <div>
              <label 
                className="block text-sm font-medium mb-2"
                style={{ color: BRAND.white }}
              >
                {t.confirmPassword}
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    if (errors.confirmPassword) setErrors({ ...errors, confirmPassword: '' });
                  }}
                  placeholder={t.confirmPasswordPlaceholder}
                  className="w-full px-4 py-3 pr-12 rounded-lg text-base focus:outline-none focus:ring-2"
                  style={{ 
                    backgroundColor: BRAND.inputBg,
                    color: BRAND.white,
                    borderColor: errors.confirmPassword ? BRAND.error : 'transparent',
                    borderWidth: '1px'
                  }}
                  data-testid="confirm-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                  style={{ color: BRAND.lightGray }}
                  data-testid="confirm-password-toggle"
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="mt-1 text-sm" style={{ color: BRAND.error }} data-testid="confirm-password-error">
                  {errors.confirmPassword}
                </p>
              )}
            </div>
          )}

          {/* Remember Me & Forgot Password (Login only) */}
          {!isRegisterMode && (
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 rounded"
                  data-testid="remember-me-checkbox"
                />
                <span className="text-sm" style={{ color: BRAND.lightGray }}>
                  {t.rememberMe}
                </span>
              </label>
              <button
                type="button"
                onClick={openForgotPassword}
                className="text-sm font-medium"
                style={{ color: BRAND.blue }}
                data-testid="forgot-password-link"
              >
                {t.forgotPassword}
              </button>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-4 rounded-lg text-lg font-bold transition-all disabled:opacity-70"
            style={{ 
              backgroundColor: BRAND.red,
              color: BRAND.white
            }}
            data-testid="login-button"
          >
            {isLoading 
              ? (isRegisterMode ? t.registering : t.signingIn) 
              : (isRegisterMode ? t.createAccount : t.signIn)
            }
          </button>
        </form>

        {/* Toggle Login/Register */}
        <p className="mt-4 text-sm" style={{ color: BRAND.lightGray }}>
          {isRegisterMode ? t.haveAccount : t.noAccount}{' '}
          <button 
            onClick={toggleMode}
            className="font-medium"
            style={{ color: BRAND.blue }}
            data-testid="toggle-auth-mode"
          >
            {isRegisterMode ? t.backToLogin : t.signUp}
          </button>
        </p>

        {/* Privacy Policy Link */}
        <p 
          className="mt-4 text-xs text-center"
          style={{ color: BRAND.lightGray }}
          data-testid="privacy-policy-consent"
        >
          {isRegisterMode ? 'By creating an account you agree to our ' : 'By signing in you agree to our '}
          <a 
            href="https://dragonboatprotracker.com/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
            className="underline"
            style={{ color: BRAND.lightGray }}
            data-testid="privacy-policy-link"
          >
            Privacy Policy
          </a>
        </p>
      </div>

      {/* Footer */}
      <div className="pb-6 px-6">
        {/* Slogan */}
        <p 
          className="text-center text-base font-bold tracking-wider mb-4"
          style={{ color: BRAND.white }}
          data-testid="slogan"
        >
          {t.slogan}
        </p>
      </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center px-4"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)' }}
          data-testid="forgot-password-modal"
        >
          <div 
            className="w-full max-w-md rounded-xl p-6"
            style={{ backgroundColor: BRAND.navyMedium }}
          >
            {!forgotEmailSent ? (
              <>
                <h2 
                  className="text-xl font-bold mb-2 text-center"
                  style={{ color: BRAND.white }}
                >
                  {t.forgotPasswordTitle}
                </h2>
                <p 
                  className="text-sm mb-6 text-center"
                  style={{ color: BRAND.lightGray }}
                >
                  {t.forgotPasswordSubtitle}
                </p>

                {forgotError && (
                  <div 
                    className="mb-4 p-3 rounded-lg text-center"
                    style={{ backgroundColor: `${BRAND.error}20`, color: BRAND.error }}
                    data-testid="forgot-error"
                  >
                    {forgotError}
                  </div>
                )}

                <form onSubmit={handleForgotPassword}>
                  <div className="mb-4">
                    <label 
                      className="block text-sm font-medium mb-2"
                      style={{ color: BRAND.white }}
                    >
                      {t.email}
                    </label>
                    <input
                      type="email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      placeholder={t.emailPlaceholder}
                      className="w-full px-4 py-3 rounded-lg text-base focus:outline-none focus:ring-2"
                      style={{ 
                        backgroundColor: BRAND.inputBg,
                        color: BRAND.white
                      }}
                      data-testid="forgot-email-input"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full py-3 rounded-lg font-bold transition-all disabled:opacity-70 mb-4"
                    style={{ 
                      backgroundColor: BRAND.red,
                      color: BRAND.white
                    }}
                    data-testid="forgot-submit-button"
                  >
                    {isLoading ? t.sending : t.sendResetLink}
                  </button>
                </form>

                <button
                  onClick={closeForgotPassword}
                  className="w-full text-sm font-medium"
                  style={{ color: BRAND.lightGray }}
                  data-testid="forgot-back-button"
                >
                  {t.backToLoginLink}
                </button>
              </>
            ) : (
              <div className="text-center">
                <div 
                  className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                  style={{ backgroundColor: '#4CAF5020' }}
                >
                  <Check className="w-8 h-8" style={{ color: '#4CAF50' }} />
                </div>
                <h2 
                  className="text-xl font-bold mb-2"
                  style={{ color: BRAND.white }}
                >
                  Check Your Email
                </h2>
                <p 
                  className="text-sm mb-6"
                  style={{ color: BRAND.lightGray }}
                >
                  {t.resetEmailSent}
                </p>
                <button
                  onClick={closeForgotPassword}
                  className="w-full py-3 rounded-lg font-bold transition-all"
                  style={{ 
                    backgroundColor: BRAND.red,
                    color: BRAND.white
                  }}
                  data-testid="forgot-done-button"
                >
                  {t.backToLoginLink}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginScreen_DBPRO;
